import { AbstractControl, ValidatorFn } from '@angular/forms';
import { AccountFieldsFragment, AccountForEditGQL } from 'src/app/graphql/gql';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CountrySelectorComponent } from '../../shared/country-selector/country-selector.component';
import { CountryStateSelectorComponent, CountryStateValue } from '../../shared/country-state-selector/country-state-selector.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { TabsModule } from 'primeng/tabs';
import { ToastService } from "../../toast/toast.service";
import { UpdateAccountGQL } from '../../graphql/gql';

// Custom validator for optional min/max
function optionalMinMaxValidator(min: number, max: number): ValidatorFn {
  return (control: AbstractControl) => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (value < min) {
      return { min: { min, actual: value } };
    }
    if (value > max) {
      return { max: { max, actual: value } };
    }
    return null;
  };
}

@Component({
  selector: 'app-edit-account',
  templateUrl: './edit-account.component.html',
  styleUrl: './edit-account.component.css',
  imports: [
    ButtonModule,
    CardModule,
    CheckboxModule,
    CountrySelectorComponent,
    CountryStateSelectorComponent,
    InputTextModule,
    MessageModule,
    ReactiveFormsModule,
    TabsModule
  ]
})
export class EditAccountComponent implements OnChanges, OnInit {
  @Input() account?: AccountFieldsFragment;
  @Input() success?: () => void;

  accountForm!: FormGroup;
  loading = false;
  accountData?: any;

  constructor(
    private formBuilder: FormBuilder,
    private accountForEditGQL: AccountForEditGQL,
    private updateAccountGQL: UpdateAccountGQL,
    private toastService: ToastService,
  ) { }

  ngOnInit(): void {
    this.accountForm = this.formBuilder.group({
      company: ['', [Validators.required, Validators.minLength(2), Validators.pattern('[a-zA-Z].*')]],
      street: ['', [Validators.required, Validators.minLength(2)]],
      city: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50), Validators.pattern('[a-zA-Z].*')]],
      zip: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(10)]],
      country: [null, [Validators.required]],
      countryStateComposite: [{ countryState: undefined, otherState: undefined }],
      phone: ['', Validators.compose([Validators.required, Validators.pattern(/^\+[0-9]+\.[0-9]+$/)])],
      fax: [null, [Validators.pattern(/^[0-9.\-+\s()]*$/)]],
      passwordTtl: [null, [optionalMinMaxValidator(1, 12)]],
      passwordMinLength: [null, [optionalMinMaxValidator(12, 128)]],
      forceOriginalPassword: [false]
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['account'] && this.account?.clientNumber) {
      this.loadAccountData(this.account.clientNumber);
    }
  }

  private loadAccountData(clientNumber: number) {
    this.loading = true;

    this.accountForEditGQL.fetch({ clientNumber: clientNumber.toString() }).subscribe({
      next: (result) => {
        const accountNode = result.data.accounts.edges?.[0]?.node;

        if (accountNode) {
          this.accountData = accountNode;
          this.accountForm.patchValue({
            company: accountNode.company,
            street: accountNode.street,
            city: accountNode.city,
            zip: accountNode.zip,
            country: accountNode.country,
            countryStateComposite: {
              countryState: accountNode.countryState,
              otherState: accountNode.otherState
            },
            phone: accountNode.phone,
            fax: accountNode.fax,
            passwordTtl: accountNode.passwordTtl,
            passwordMinLength: accountNode.passwordMinLength,
            forceOriginalPassword: accountNode.forceOriginalPassword
          });

          // Mark invalid controls as dirty to show validation errors immediately
          Object.keys(this.accountForm.controls).forEach(key => {
            const control = this.accountForm.get(key);
            if (control && control.invalid) {
              control.markAsDirty();
            }
          });
        } else {
          this.toastService.show('error', $localize`Account not found.`);
        }

        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading account:', error);
        this.toastService.show('error', $localize`Failed to load account data.`);
        this.loading = false;
      }
    });
  }

  submitForm() {
    if (this.accountForm.invalid || !this.accountData) {
      return;
    }

    const formValue = this.accountForm.value;
    const composite: CountryStateValue = formValue.countryStateComposite;

    this.updateAccountGQL.mutate({
      clientNumber: this.accountData.clientNumber,
      company: formValue.company,
      street: formValue.street,
      city: formValue.city,
      zip: formValue.zip,
      country: formValue.country,
      countryState: composite.countryState,
      otherState: composite.otherState,
      phone: formValue.phone,
      fax: formValue.fax,
      passwordTtl: formValue.passwordTtl,
      passwordMinLength: formValue.passwordMinLength,
      forceOriginalPassword: formValue.forceOriginalPassword
    }).subscribe({
      next: (result) => {
        if (result.errors) {
          this.toastService.show('error', $localize`Failed to update account.`);
        } else {
          this.toastService.show('success', $localize`Account updated successfully.`);
          this.success?.();
        }
      },
      error: (error) => {
        console.error('Error updating account:', error);
        this.toastService.show('error', $localize`Failed to update account.`);
      }
    });
  }
}